import {
  AcceptChallengeResultTypenames,
  ChallengeType,
  ProfileChangeResultTypenames,
  ProfileChangeTypeInput,
  Query,
  SelectChallengeResultTypenames,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import useProfileChange from 'features/myVaro/hooks/useProfileChange';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useAcceptChallengeGql from 'features/otp/hooks/useAcceptChallengeGql';
import useSelectChallengeGql from 'features/otp/hooks/useSelectChallengeGql';
import OtpScreen from 'features/otp/OtpScreen';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GetProfileInfo from 'services/graphql/queries/GetProfileInfo';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import delay from 'shared/utils/delay/delay';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { clearPhoneNumberFormatting } from 'shared/utils/string/phone';

const VERIFY_SCREEN_ERROR_CODES = [500, 400];
const POLLING_DELAY_MS = 2000;

interface SearchParams extends Record<string, string | string[]> {
  challengeId: string;
  phoneNumber: string;
  flowKey: OtpFlowKey;
}

const VerifyPhoneNumberScreen = () => {
  const router = useRouter();
  const { t: translatedTextOtp } = useTranslation('otp');
  const { challengeId, phoneNumber, flowKey } =
    useLocalSearchParams<SearchParams>();
  const [isPolling, setIsPolling] = useState(false);
  const lastFourPhoneDigits = phoneNumber
    ? (phoneNumber?.slice(-4) as string)
    : '';
  const [executeProfileInfoQuery, abortCurrentProfileInfoQuery] =
    useCancellableQuery<Query>(GetProfileInfo);
  const abortControllerRef = useRef<AbortController | null>(null);
  const {
    acceptChallenge,
    fetching: isAcceptChallengeLoading,
    apiError,
  } = useAcceptChallengeGql();
  const { fetching: resendingOtp, selectChallenge } = useSelectChallengeGql();

  const {
    fetching: isProfileChangeLoading,
    error: profileChangeError,
    profileChange,
  } = useProfileChange();

  // Poll for phone number update
  const waitForPhoneNumberUpdate = async (
    expectedPhone: string,
    signal: AbortSignal,
    maxAttempts = 10,
  ): Promise<boolean> => {
    const unformattedPhone = clearPhoneNumberFormatting(expectedPhone);

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // If already aborted, return early
      if (signal.aborted) {
        return false;
      }

      try {
        const data = await executeProfileInfoQuery(
          {},
          { requestPolicy: 'network-only' },
        );

        const currentPhone = data.user?.customerInfo?.phone;

        if (currentPhone === unformattedPhone) {
          return true; // Phone number updated successfully
        }
      } catch (err) {
        // If the controller is aborted, simply return false
        if (err instanceof AbortError || signal.aborted) {
          return false;
        }

        logErrorWithContext(err, {
          featureArea: FeatureArea.FRAUD,
          responsibleTeam: ResponsibleTeam.FRAUD,
          severity: ErrorSeverity.LOW,
        });
        // Continue polling despite errors
      }

      // Wait before next attempt
      await delay(POLLING_DELAY_MS, signal);
    }

    return false; // Max attempts reached without success
  };

  const stopPolling = () => {
    abortControllerRef.current?.abort();
    abortCurrentProfileInfoQuery();
  };

  const trackPhoneChangeSuccess = usePressableAnalytics({
    analyticsId: 'phone-number-changed',
  });

  const handlePostSuccess = () => {
    trackPhoneChangeSuccess();
    setIsPolling(false);
    stopPolling();
    router.dismissTo({
      pathname: '/myvaro/personal-details',
      params: { refresh: 'true', msgKey: 'phoneChanged' },
    });
  };

  const onBack = () => {
    setIsPolling(false);
    stopPolling();
    router.dismiss();
  };

  // Handle hardware back button press
  useBackHandler(() => {
    onBack();
    return true;
  });

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleVerifyOtp = async (code: string) => {
    try {
      const result = await acceptChallenge({
        input: {
          challengeId: String(challengeId),
          acceptOptions: {
            otpOption: {
              otpCode: code,
            },
          },
        },
      });
      const challengeResult = result.data?.acceptChallenge;

      if (
        challengeResult?.__typename ===
          AcceptChallengeResultTypenames.AcceptChallengeSuccess &&
        challengeResult.challengeVerified
      ) {
        const response = await profileChange(
          { phoneNumberChangeRequest: { challengeId: challengeId } },
          ProfileChangeTypeInput.PhoneNumberChange,
        );
        const phoneChangeResult = response.data?.profileChange;

        if (
          phoneChangeResult?.__typename ===
          ProfileChangeResultTypenames.ProfileChangeSuccess
        ) {
          const isPhoneChange = Boolean(phoneChangeResult.updated);
          if (!isPhoneChange) {
            if (
              phoneChangeResult.challengeType === ChallengeType.TokenLinkEmail
            ) {
              router.navigate({
                pathname:
                  '/(authenticated)/myvaro/personal-details/change-phone/check-email',
                params: { challengeId: phoneChangeResult.challengeId },
              });
            }
          } else {
            // Start polling for phone number update
            setIsPolling(true);
            // Abort any previous polling and create a new abort controller
            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            const isUpdated = await waitForPhoneNumberUpdate(
              phoneNumber,
              abortController.signal,
              10,
            );

            if (abortController.signal.aborted) {
              return {};
            }

            setIsPolling(false);

            if (isUpdated) {
              return {
                success: true,
              };
            }

            // Phone update initiated but not confirmed yet
            // Still return success but log this situation
            logErrorWithContext(
              new Error('Phone number update not confirmed after polling'),
              {
                featureArea: FeatureArea.FRAUD,
                responsibleTeam: ResponsibleTeam.FRAUD,
                severity: ErrorSeverity.LOW,
              },
            );

            return {
              success: true,
            };
          }
        } else if (
          phoneChangeResult?.__typename ===
          ProfileChangeResultTypenames.ProfileChangeError
        ) {
          return {
            errorCode: Number(phoneChangeResult.code),
            message: phoneChangeResult.message,
            title: phoneChangeResult.title,
          };
        } else if (
          phoneChangeResult?.__typename ===
          ProfileChangeResultTypenames.ProfileChangeDeclined
        ) {
          router.navigate(
            '/(authenticated)/myvaro/personal-details/change-phone/decline',
          );
        }
      } else if (
        challengeResult?.__typename ===
        AcceptChallengeResultTypenames.AcceptChallengeError
      ) {
        return {
          errorCode: Number(challengeResult.code),
          message: challengeResult.message,
          title: challengeResult.title,
        };
      }

      const errors = profileChangeError || apiError;
      if (errors) {
        return {
          errorCode: 500,
          message: errors?.message,
          title: errors?.name,
        };
      }
      // Default fallback if result doesn't match expected shapes
      return {
        errorCode: 500,
        message: 'Unexpected error occurred',
        title: 'Verification Failed',
      };
    } catch (error) {
      if (error instanceof AbortError) {
        return {};
      }
      logErrorWithContext(error, {
        featureArea: FeatureArea.FRAUD,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.LOW,
      });
      return {};
    }
  };

  // Handler passed to OtpScreen for resend
  const handleResendOtp = async () => {
    try {
      const result = await selectChallenge(challengeId.toString());
      const challengeResult = result.data?.selectChallenge;
      if (
        challengeResult?.__typename ===
        SelectChallengeResultTypenames.SelectChallengeError
      ) {
        return {
          errorCode: Number(challengeResult.code),
          message: challengeResult.message,
          title: challengeResult.title,
        };
      }
      return {};
    } catch (error) {
      if (error instanceof AbortError) {
        return {};
      }
      logErrorWithContext(error, {
        featureArea: FeatureArea.FRAUD,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.LOW,
      });
      return {};
    }
  };

  const description = `${translatedTextOtp('common.description')}${String(lastFourPhoneDigits)}.`;

  return (
    <OtpScreen
      flowKey={flowKey}
      verificationType={OtpVerificationType.PHONE}
      // Text content
      description={description}
      // External state
      isLoading={
        resendingOtp ||
        isAcceptChallengeLoading ||
        isProfileChangeLoading ||
        isPolling
      }
      // Configurable error codes
      screenLevelErrorCodes={VERIFY_SCREEN_ERROR_CODES}
      // Event handlers
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={onBack}
      onPostSuccess={handlePostSuccess}
    />
  );
};
export default VerifyPhoneNumberScreen;
